import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = '',
  description,
  keywords,
  canonical,
  ogImage = 'https://lirp.cdn-website.com/10fc801d/dms3rep/multi/opt/JPR+Customs-Logo-1920w.png',
  ogType = 'website',
  structuredData
}) => {
  const siteName = 'JPR Customs';
  const baseUrl = 'https://www.jprcustoms.com.au';
  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} - Custom Car Fabrication & Spray Painting Murray Bridge SA`;
  
  const defaultDescription = 'JPR Customs provides expert car spray painting, crash repairs, panel beating, custom paint work, vehicle restoration, and mechanical repairs in Murray Bridge, South Australia. Quality automotive services with decades of experience.';
  const metaDescription = description || defaultDescription;
  
  const defaultKeywords = 'spray painting, car spray painting, crash repairs, panel beating, car restoration, custom paint, vehicle accessories, towing services, mechanical repairs, Murray Bridge, South Australia, auto body shop, collision repairs, paint resprays, classic car restoration, vintage car restoration, bull bars, canopies, trays';
  const metaKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  // Set title using useEffect as workaround for Helmet bug with React 19
  useEffect(() => {
    document.title = fullTitle;
    
    // Set structured data
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo-structured]');
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-structured', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [fullTitle, structuredData]);

  return (
    <Helmet>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content="JPR Customs" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Geographic Meta Tags */}
      <meta name="geo.region" content="AU-SA" />
      <meta name="geo.placename" content="Murray Bridge" />
      <meta name="geo.position" content="-35.1203;139.2728" />
      <meta name="ICBM" content="-35.1203, 139.2728" />
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={`${baseUrl}${canonical}`} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical ? `${baseUrl}${canonical}` : baseUrl} />
      <meta property="og:locale" content="en_AU" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

// Local Business Schema for the main site
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "AutoBodyShop",
  "name": "JPR Customs",
  "alternateName": "JPR Customs Paint Panel Restorations",
  "description": "Expert car spray painting, crash repairs, panel beating, custom paint work, vehicle restoration, mechanical repairs, and towing services in Murray Bridge, South Australia.",
  "url": "https://www.jprcustoms.com.au",
  "logo": "https://lirp.cdn-website.com/10fc801d/dms3rep/multi/opt/JPR+Customs-Logo-1920w.png",
  "image": "https://lirp.cdn-website.com/10fc801d/dms3rep/multi/opt/JPR+Customs-Logo-1920w.png",
  "telephone": "+61417119544",
  "email": "admin@jprcustoms.com.au",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "13 Hindmarsh Rd",
    "addressLocality": "Murray Bridge",
    "addressRegion": "SA",
    "postalCode": "5253",
    "addressCountry": "AU"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": -35.1203,
    "longitude": 139.2728
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:30",
      "closes": "16:30"
    }
  ],
  "priceRange": "$$",
  "paymentAccepted": "Cash, Credit Card, EFTPOS",
  "areaServed": {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": -35.1203,
      "longitude": 139.2728
    },
    "geoRadius": "100000"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Automotive Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Car Spray Painting",
          "description": "Professional spray painting and resprays for all vehicles"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Crash Repairs",
          "description": "Collision repair and insurance claim services"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Panel Beating",
          "description": "Expert panel beating and dent removal"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Classic Car Restoration",
          "description": "Vintage and classic car restoration services"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Mechanical Repairs",
          "description": "Full mechanical repair and servicing"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Towing Services",
          "description": "24/7 towing and roadside assistance"
        }
      }
    ]
  },
  "sameAs": [
    "https://www.facebook.com/profile.php?id=100075571521893",
    "https://www.instagram.com/j.p.r.customs",
    "https://www.linkedin.com/in/j-p-r-customs-291976167"
  ]
};

// Service Schema Generator
export const createServiceSchema = (serviceName, serviceDescription, serviceUrl) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": serviceName,
  "name": serviceName,
  "description": serviceDescription,
  "url": serviceUrl,
  "provider": {
    "@type": "AutoBodyShop",
    "name": "JPR Customs",
    "telephone": "+61417119544",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "13 Hindmarsh Rd",
      "addressLocality": "Murray Bridge",
      "addressRegion": "SA",
      "postalCode": "5253",
      "addressCountry": "AU"
    }
  },
  "areaServed": {
    "@type": "State",
    "name": "South Australia"
  }
});

export default SEO;
